/**
 * FilterBar — Controlled Component fuer alle Listen-Filter (UI-04).
 *
 * Pattern: kein interner State — MainPage haelt FilterState, FilterBar ruft onChange auf.
 */

import { useState } from 'react';
import { ChevronDown } from 'lucide-react';

export interface FilterState {
  status: string[];
  hasPackage: 'all' | 'ja' | 'nein';
  carrier: 'all' | 'DHL' | 'GLS';
  onlyStuck: boolean;
  search: string;
}

interface FilterBarProps {
  filters: FilterState;
  onChange: (f: FilterState) => void;
}

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'open', label: 'Offen' },
  { value: 'awaiting_package', label: 'Wartet auf Paket' },
  { value: 'carrier', label: 'Bei Carrier' },
  { value: 'manual_only', label: 'Nur manuell' },
  { value: 'done', label: 'Abgeschlossen' },
  { value: 'closed_manually', label: 'Manuell geschlossen' },
];

export default function FilterBar({ filters, onChange }: FilterBarProps) {
  const [statusOpen, setStatusOpen] = useState(false);

  const toggleStatus = (value: string) => {
    const next = filters.status.includes(value)
      ? filters.status.filter(s => s !== value)
      : [...filters.status, value];
    onChange({ ...filters, status: next });
  };

  const reset = () => onChange({ status: [], hasPackage: 'all', carrier: 'all', onlyStuck: false, search: '' });

  const activeFilterCount =
    filters.status.length +
    (filters.hasPackage !== 'all' ? 1 : 0) +
    (filters.carrier !== 'all' ? 1 : 0) +
    (filters.onlyStuck ? 1 : 0) +
    (filters.search.trim() ? 1 : 0);

  return (
    <div className="border border-[#DAD9D9] rounded-lg p-4 bg-white space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-[#1A1A1A]">
          Filter {activeFilterCount > 0 && <span className="text-xs text-gray-500">({activeFilterCount} aktiv)</span>}
        </span>
        {activeFilterCount > 0 && (
          <button
            type="button"
            onClick={reset}
            className="text-xs text-gray-600 hover:text-[#1A1A1A] underline"
          >
            Zuruecksetzen
          </button>
        )}
      </div>

      {/* Suchfeld — sucht in Auftrags-Nr, Paketnummer, Kontakt-ID */}
      <input
        type="text"
        value={filters.search}
        onChange={(e) => onChange({ ...filters, search: e.target.value })}
        placeholder="Suchen: Auftrags-Nr, Paketnummer oder Kontakt-ID..."
        className="w-full px-3 py-2 text-sm border border-[#DAD9D9] rounded focus:outline-none focus:border-[#1A1A1A]"
      />

      <div className="flex flex-wrap items-start gap-4">
        {/* Status Multi-Select (Disclosure) */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setStatusOpen(v => !v)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm border border-[#DAD9D9] rounded hover:bg-gray-50"
          >
            Status {filters.status.length > 0 && <span className="text-xs text-gray-500">({filters.status.length})</span>}
            <ChevronDown className="w-3 h-3" />
          </button>
          {statusOpen && (
            <div className="absolute z-10 mt-1 bg-white border border-[#DAD9D9] rounded shadow-lg p-2 space-y-1 min-w-[200px]">
              {STATUS_OPTIONS.map(opt => (
                <label key={opt.value} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded">
                  <input
                    type="checkbox"
                    checked={filters.status.includes(opt.value)}
                    onChange={() => toggleStatus(opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Paketnummer */}
        <div className="flex items-center gap-1 text-sm">
          <span className="text-gray-600">Paket:</span>
          {(['all', 'ja', 'nein'] as const).map(v => (
            <button
              key={v}
              type="button"
              onClick={() => onChange({ ...filters, hasPackage: v })}
              className={`px-2 py-1 text-xs rounded transition-colors ${
                filters.hasPackage === v
                  ? 'bg-[#1A1A1A] text-white'
                  : 'bg-white border border-[#DAD9D9] text-[#1A1A1A] hover:bg-gray-50'
              }`}
            >
              {v === 'all' ? 'Alle' : v === 'ja' ? 'Ja' : 'Nein'}
            </button>
          ))}
        </div>

        {/* Carrier */}
        <div className="flex items-center gap-1 text-sm">
          <span className="text-gray-600">Carrier:</span>
          {(['all', 'DHL', 'GLS'] as const).map(v => (
            <button
              key={v}
              type="button"
              onClick={() => onChange({ ...filters, carrier: v })}
              className={`px-2 py-1 text-xs rounded transition-colors ${
                filters.carrier === v
                  ? 'bg-[#1A1A1A] text-white'
                  : 'bg-white border border-[#DAD9D9] text-[#1A1A1A] hover:bg-gray-50'
              }`}
            >
              {v === 'all' ? 'Alle' : v}
            </button>
          ))}
        </div>

        {/* Stuck-Toggle */}
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={filters.onlyStuck}
            onChange={(e) => onChange({ ...filters, onlyStuck: e.target.checked })}
          />
          <span className="text-[#1A1A1A]">Nur stuck</span>
        </label>
      </div>
    </div>
  );
}
